import { DateTime } from 'luxon';

/**
 * Get today's date in the user's timezone, set to the start of the day
 * @returns {DateTime} Today's date as a Luxon DateTime object
 */
export function getTodayInLocalTimezone() {
  return DateTime.local().startOf('day');
}

/**
 * Convert a UTC date to the user's timezone
 * @param {Date} utcDate - Date object in UTC
 * @returns {DateTime} Date in user's timezone as a Luxon DateTime object
 */
export function utcToLocalTimezone(utcDate) {
  return DateTime.fromJSDate(utcDate, { zone: 'utc' }).toLocal().startOf('day');
}

/**
 * Format a date for display
 * @param {DateTime} date - Luxon DateTime object
 * @param {string} format - Format to use (e.g., 'DATE_MED', 'DATE_FULL')
 * @returns {string} Formatted date string
 */
export function formatDate(date, format = DateTime.DATE_MED) {
  return date.toLocaleString(format);
}

/**
 * Check if a date is today
 * @param {DateTime} date - Luxon DateTime object
 * @returns {boolean} True if the date is today
 */
export function isToday(date) {
  return date.hasSame(getTodayInLocalTimezone(), 'day');
}

/**
 * Get a range of dates
 * @param {DateTime} startDate - Start date
 * @param {DateTime} endDate - End date
 * @returns {DateTime[]} Array of dates between start and end (inclusive)
 */
export function getDateRange(startDate, endDate) {
  const dates = [];
  let currentDate = startDate;

  while (currentDate <= endDate) {
    dates.push(currentDate);
    currentDate = currentDate.plus({ days: 1 });
  }

  return dates;
}

/**
 * Get the date range for the last N days
 * @param {number} days - Number of days to look back
 * @returns {DateTime[]} Array of dates for the last N days
 */
export function getLastNDays(days) {
  const endDate = getTodayInLocalTimezone();
  const startDate = endDate.minus({ days: days - 1 });
  return getDateRange(startDate, endDate);
}

/**
 * Group dates by week
 * @param {DateTime[]} dates - Array of dates to group
 * @returns {Object} Object with week numbers as keys and arrays of dates as values
 */
export function groupByWeek(dates) {
  // eslint-disable-next-line unicorn/no-array-reduce
  return dates.reduce((groups, date) => {
    const weekNumber = date.weekNumber;
    if (!groups[weekNumber]) {
      groups[weekNumber] = [];
    }
    groups[weekNumber].push(date);
    return groups;
  }, {});
}

/**
 * Calculate the number of days between two dates
 * @param {DateTime} startDate - Start date
 * @param {DateTime} endDate - End date
 * @returns {number} Number of days between the dates
 */
export function daysBetween(startDate, endDate) {
  return Math.floor(endDate.diff(startDate, 'days').days);
}

/**
 * Check if a date is within a specified range
 * @param {DateTime} date - Date to check
 * @param {DateTime} startDate - Start of range
 * @param {DateTime} endDate - End of range
 * @returns {boolean} True if date is within range (inclusive)
 */
export function isDateInRange(date, startDate, endDate) {
  return date >= startDate && date <= endDate;
}

/**
 * Get the start and end of a week containing the given date
 * @param {DateTime} date - Date within the week
 * @returns {Object} Object containing start and end dates of the week
 */
export function getWeekBounds(date) {
  const start = date.startOf('week');
  const end = date.endOf('week');
  return { start, end };
}

/**
 * Format a date relative to today (e.g., "today", "yesterday", "2 days ago")
 * @param {DateTime} date - Date to format
 * @returns {string} Relative date string
 */
export function getRelativeDate(date) {
  const today = getTodayInLocalTimezone();
  const diff = daysBetween(date, today);

  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff} days ago`;
  if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
  if (diff < 365) return `${Math.floor(diff / 30)} months ago`;
  return `${Math.floor(diff / 365)} years ago`;
}
